import { useThrottleFn } from "ahooks";
import { CenterPopup, CheckList, Divider } from "antd-mobile";
import { Dialog } from "@haidilao-private/mobile-design";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { trackLog } from "@/api/common";
import {
	couponActiveDetail,
	getGt4Captcha,
	getVerifyCode,
	loginAndSend,
	shareCouponSend,
} from "@/api/coupon";
import HLoading from "@/components/HLoading";
import { templateReplace, fetchTranslate } from "@/locales";
import { ISO_TO_ZH_LOCALE_KEY } from "@/locales/generatedIsoToZhLocaleKey";
import { UPDATE_COMMON_STATE } from "@/reducers/Common/constant";
import { $vw } from "@/utils";
import {
	areaCodeDataList,
	countryDefaultLanguageMap,
	languageSelectList,
} from "@/utils/countryCode";
import HdlToast from "@/utils/toast";
import useCallbackState from "./useCallbackState";
import "./index.scss";
import Cookies from "js-cookie";
import { isInApp, isLine, navigateToCoupon } from "@/utils";
import { getLineLiffCouponUrl, goToPage, LOGIN_EMAIL } from "./util";
import LoginCouponModal, {
	type AreaCodeOption,
} from "./components/LoginCouponModal";

// 使用原生触摸点击，避免 FastClick 拦截输入框的聚焦和软键盘唤起。
let isCodeType: boolean = false;
const phoneReg: RegExp = /^\d{8,11}$/;
const emailReg: RegExp = /^[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

type TrackIdentifier =
	| "h5_active_couponpage_view"
	| "h5_active_couponpage_click"
	| "h5_active_smscode_success_show"
	| "h5_active_share_coupon_success_show";
type TrackType = "page" | "click" | "show";
type TrackEventName =
	| "H5 领取优惠券页面浏览"
	| "H5 领取优惠券页面按钮点击"
	| "H5 领取优惠券-短信验证码发送成功"
	| "H5 领取优惠券-邮箱验证码发送成功"
	| "H5 领取优惠券-分享优惠券成功";

const Fail_Ver_Code = "-50011";
const Time_End = "-50012";
const Not_Get_Repeat = "-50013";
const Not_Get_Eligible = "-50014";
const Send_Code_Error = "-50015";
const Network_Error = "-50016";
const Member_Status_Abnormal = "-50017";
const Send_Code_Frequently = "-50018";
const Activity_Boom = "-50019";
const ERR_NETWORK = "ERR_NETWORK";
// 错误状态
/** 值为 locales 中文 key（与 translateSource「简体中文」列一致） */
const errorStatus: any = {
	[Fail_Ver_Code]: "验证码错误",
	[Time_End]: "活动已结束",
	[Not_Get_Repeat]: "无法重复领取！",
	[Not_Get_Eligible]: "无法领取，不符合活动条件",
	[Send_Code_Error]: "发送验证码错误",
	[Network_Error]: "您当前的网络异常，请稍后再试",
	[Member_Status_Abnormal]: "会员账号异常，无法领取",
	[Send_Code_Frequently]:
		"验证码发送次数过多。请输入最后收到的验证码，或稍后再试。",
	[Activity_Boom]: "领取人数过多，请稍候再试",
	[ERR_NETWORK]: "您当前的网络异常，请稍后再试",
};
/* 验证码倒计时时长 */
const TIME_DURING = 90;
const SIMPLIFIED_CHINESE_LANGUAGE_CODE = "CN";
const SIMPLIFIED_CHINESE_LANGUAGE_INFO = {
	value: "简体中文",
	languageName: "简体中文",
	languageCode: SIMPLIFIED_CHINESE_LANGUAGE_CODE,
	captchaLanguage: "zh-cn",
};
const getTranslateWithChineseFallback = (locales: Record<string, string> = {}) =>
	new Proxy(locales, {
		get(target, key) {
			if (typeof key !== "string") {
				return Reflect.get(target, key);
			}
			return target[key] || key;
		},
	});
const ReceiveCoupon: React.FC = (props: any) => {
	const [searchParams] = useSearchParams();
	const dispatch = useDispatch();
	const loadingRef = useRef<any>(); //loading
	// 翻译文案
	const [translate, setTranslate] = useState<any>(
		getTranslateWithChineseFallback(),
	);
	const translateVal = useRef<any>(getTranslateWithChineseFallback());
	// 国家, 隐私协议、展示需要
	const [country, setCountry] = useState("CN");
	// 活动需要根据语言调整bg
	const [detailRuleBg, setDetailRuleBg] = useState("");
	//是否展示登录弹框
	const [isImmediately, setImmediately] = useCallbackState(false);
	//是否同意用户协议和隐私政策
	const [isAgree, setAgree] = useState(false);
	//是否同意推送
	const [isAgreePush, setAgreePush] = useState(false);
	// 语言面板
	const [languageVisible, setLanguageVisible] = useState(false);
	// 展示选择的语言
	const [languageValue, setLanguageValue] = useState(
		SIMPLIFIED_CHINESE_LANGUAGE_INFO.value,
	);
	// 选择区号后，关闭弹框
	const hadChangeLanguage = useRef(false);
	// 展示选择的语言的隐私协议
	const languageCode = useRef(SIMPLIFIED_CHINESE_LANGUAGE_CODE);
	//手机号
	const [phone, setPhone] = useState("");
	const phoneVal = useRef("");
	// 邮件
	const [email, setEmail] = useState("");
	// 登录方式
	const [loginType, setLoginType] = useState(LOGIN_EMAIL);
	const [isEmailLogin, setIsEmailLogin] = useState(loginType == LOGIN_EMAIL);
	//验证码
	const [verifyCode, setVerifyCode] = useState("");
	// 排序的区号
	const [sortAreaCodeDataList, setSortAreaCodeDataList] =
		useState(areaCodeDataList);
	//区号
	const [phoneAreaCode, setPhoneAreaCode] = useState("+86");
	const phoneAreaCodeVal = useRef("+86");
	//活动状态
	const [actFailStatus, setActFailStatus] = useState(false);
	const activeInfo = useRef<any>({});
	/* 国家信息语言 */
	const [languageSelectedInfo, setLanguageSelectedInfo] = useState<any>(
		SIMPLIFIED_CHINESE_LANGUAGE_INFO,
	);
	const captchaLanguage = useRef("eng");

	/* 验证码倒计时 */
	const [verCodeSecond, setVerCodeSecond] = useState(TIME_DURING);
	const [isGetVerCode, setIsGetVerCode] = useState(false);
	const getVerCodeTimer = useRef<any>(null);
	const currentTime = useRef(TIME_DURING);
	/** 邮箱获取验证码点击标记 */
	const emailLoginVercodeBtnFlag = useRef(false);

	/* 滑块 */
	const catptchInstance = useRef<any>(null);
	const riskTypeConfig = useRef("");

	const [getSuccessDialog, setGetSuccessDialog] = useState(false);
	const [aiTranslateVisible, setAiTranslateVisible] = useState(false);
	const [aiTranslateSelectedLanguageInfo, setAiTranslateSelectedLanguageInfo] =
		useState<any>();
	const [aiTranslateFallbackLanguageInfo, setAiTranslateFallbackLanguageInfo] =
		useState<any>();

	// 初始化和手动切换共用确认规则，取消时使用国家默认语言。
	const showAiTranslateConfirm = (
		selectedLanguageInfo: (typeof languageSelectList)[number],
		activityCountry: string,
	) => {
		const countryLanguageInfo = languageSelectList.find(
			(language) =>
				language.languageCode === countryDefaultLanguageMap[activityCountry],
		);

		if (
			selectedLanguageInfo.languageCode !== SIMPLIFIED_CHINESE_LANGUAGE_CODE &&
			selectedLanguageInfo.languageCode !== "EN" &&
			selectedLanguageInfo.languageCode !== countryLanguageInfo?.languageCode
		) {
			setAiTranslateSelectedLanguageInfo(selectedLanguageInfo);
			setAiTranslateFallbackLanguageInfo(countryLanguageInfo);
			setAiTranslateVisible(true);
			return true;
		}
		return false;
	};

	const applyLanguageInfo = (selectedLanguageInfo: any) => {
		if (!selectedLanguageInfo) {
			return;
		}

		setLanguageValue(selectedLanguageInfo.value);
		const _defaultInfo =
			activeInfo?.current?.languageImgVos?.filter(
				(_item: any) => _item.isDefault,
			)[0] || {};
		const _activeInfo =
			activeInfo?.current?.languageImgVos?.filter(
				(_item: any) => _item.language == selectedLanguageInfo?.languageCode,
			)[0] || _defaultInfo;

		setDetailRuleBg(_activeInfo.url);
		setLanguageSelectedInfo(selectedLanguageInfo);
		captchaLanguage.current = selectedLanguageInfo.captchaLanguage || "eng";
		languageCode.current = selectedLanguageInfo.languageCode;
		loadTranslate(selectedLanguageInfo.languageCode || "EN");

		if (!isEmailLogin) {
			initCaptcha();
		}
	};

  const loadTranslate = (language: string) => {
    console.log("loadTranslate", language);
		return fetchTranslate(language || "EN")
			.then((locales) => {
				const translateWithFallback =
					getTranslateWithChineseFallback(locales);
				setTranslate(translateWithFallback);
				translateVal.current = translateWithFallback;
				return translateWithFallback;
			})
			.catch((err) => {
				console.error("load translate failed:", err);
			});
	};

	/**
	 * 获取活动详情
	 */
	const getActivityDetail = (activityId: string) => {
		loadingRef.current.open();
		couponActiveDetail({
			activityId,
		})
			.then((res: any) => {
				console.log(res, "======================");
				loadingRef.current.close();
				// setActStatus(false)
				if (res.code == 200) {
					// 获取滑块配置
					getCaptchaConfigFn();
					const {
						country,
						timezone,
						languageImgVos,
						activityName,
						activityStatus,
						templateIdList,
						distributedComplete,
					} = res.data;
					setCountry(country || "CN");
					dispatch({
						type: UPDATE_COMMON_STATE,
						payload: {
							country: country || "CN",
							timezone: timezone || "CTT",
						},
					});
					// 接口返回的指定默认数据
					const _defaultInfo = languageImgVos?.filter(
						(_item: any) => _item.isDefault,
					)[0];
					setDetailRuleBg(_defaultInfo.url);

					const selectedLanguageInfo = languageSelectList.find(
						(language) => language.languageCode === _defaultInfo.language,
          )!;

					setLanguageValue(selectedLanguageInfo.value);
					setLanguageSelectedInfo(selectedLanguageInfo);
					captchaLanguage.current = selectedLanguageInfo.captchaLanguage;
					languageCode.current = _defaultInfo.language;
					loadTranslate(_defaultInfo.language);
					activeInfo.current = JSON.parse(JSON.stringify(res.data));

					trackLogFn(
						"h5_active_couponpage_view",
						"page",
						"H5 领取优惠券页面浏览",
						selectedLanguageInfo.languageName,
					);
					// 活动结束或者活动没结束，但是券发没了
					if (
						activityStatus != 2 ||
						(activityStatus == 2 && distributedComplete)
					) {
						setActFailStatus(true);
						return;
          }

					showAiTranslateConfirm(selectedLanguageInfo, country);
				} else {
					HdlToast.info(res?.msg || (translate && translate["慢了一步，活动已经结束了"]));
					setActFailStatus(true);
				}
			})
			.catch((err) => {
				const _errorMsg =
					(err.code &&
						errorStatus[err.code] &&
						translate &&
						translate[errorStatus[err.code]]) ||
					err.msg ||
					err.message ||
					(translate && translate["慢了一步，活动已经结束了"]);
				HdlToast.info(_errorMsg);
				// err.code && HdlToast.info((translate && translate[errorStatus[err.code]]) || err.msg || (translate && translate['not_get_late']));
				setActFailStatus(true);
				loadingRef.current.close();
				console.log({ err });
			});
	};
	//处理本地数据
	useEffect(() => {
		const activityId = searchParams?.get("activityId");
		activityId && getActivityDetail(activityId);
	}, [searchParams]);

	useEffect(() => {
		if (isImmediately) {
			/** 获取滑块配置 */
			// getCaptchaConfigFn();
			/** 区号排序 */
			sortAreaCodeListFn();
		}
	}, [isImmediately]);

	/* 手机区号 */
	useEffect(() => {
		// if(hadChangeLanguage.current) return;
		if (translate) {
			document.title =
				(translate && translate["领取海底捞专属优惠券"]) || "领取海底捞专属优惠券";
		}
	}, [phoneAreaCode, translate]);

	useEffect(() => {
		setIsEmailLogin(loginType == LOGIN_EMAIL);
		if (loginType != LOGIN_EMAIL) {
			initCaptcha();
		}
	}, [loginType]);

	const selectLanguageFn = (val: any) => {
		setLanguageVisible(false);
		const _selectedLanguageValue = val[0];
		/* 切换语言跟之前的一样 */
		if (!_selectedLanguageValue) {
			return;
		}
		// console.log({_selectedLanguageValue, languageValue, val},)
		setLanguageVisible(false);
		/* 根据语言value，兑换国家编码 */
		const _selectedLanguageInfo = languageSelectList.filter(
			(_lan: any) => _lan.value == _selectedLanguageValue,
		)[0];
		console.log({ _selectedLanguageInfo }, "==============");
		if (
			showAiTranslateConfirm(_selectedLanguageInfo, activeInfo.current?.country)
		) {
			return;
		}
		applyLanguageInfo(_selectedLanguageInfo);
		// setPhoneAreaCode(val[0] as string);
		// translate
	};

	const sortAreaCodeListFn = () => {
		const areaCodeDataListCpoy = JSON.parse(JSON.stringify(areaCodeDataList));

		const targetCountry = country;
		const targetIndex = areaCodeDataListCpoy.findIndex(
			(_areaInfo: any) => _areaInfo.country === targetCountry,
		);

		if (targetIndex !== -1 && targetIndex !== 0) {
			// 使用 splice 移除对象，并使用 unshift 将其添加到第一位
			const targetArea = areaCodeDataListCpoy.splice(targetIndex, 1)[0];
			areaCodeDataListCpoy.unshift(targetArea);
			if (!hadChangeLanguage.current) {
				setPhoneAreaCode(targetArea.code);
				phoneAreaCodeVal.current = targetArea.code;
			}
		}
		setSortAreaCodeDataList(areaCodeDataListCpoy);
	};
	/**
	 * 领券成功后
	 */
	const reset = (type?: string) => {
		setImmediately(false);
		setAgree(false);
		setAgreePush(false);
		setPhone("");
		phoneVal.current = "";
		setVerifyCode("");
		setIsGetVerCode(false);
		getVerCodeTimer.current && clearInterval(getVerCodeTimer.current);
		currentTime.current = TIME_DURING;
		setVerCodeSecond(TIME_DURING);
		hadChangeLanguage.current = false;
		if (type !== "closeLoginModal") {
			getCaptchaConfigFn();
		}
		if (type === "closeLoginModal") {
			const defaultAreaCode =
				areaCodeDataList.find((_areaInfo: any) => _areaInfo.country === country)
					?.code || "+86";
			setPhoneAreaCode(defaultAreaCode);
			phoneAreaCodeVal.current = defaultAreaCode;
		}
		setEmail("");
		setLoginType(LOGIN_EMAIL);
		emailLoginVercodeBtnFlag.current = false;
		if (type !== "closeLoginModal") {
			setGetSuccessDialog(false);
		}
	};
	const selectLoginTypeFn = (val: any) => {
		console.log({ val }, "val ====selectLoginTypeFn");
		const loginType = val[0];
		if (!loginType) {
			return;
		}
		if (loginType == LOGIN_EMAIL) {
			setPhone("");
		} else {
			setEmail("");
		}
		setLoginType(val[0]);
	};
	const closeLoginModal = () => {
		reset("closeLoginModal");
	};
	const handlePhoneChange = (value: string) => {
		if ((/^[0-9]*$/.test(value) && value.length < 12) || !value) {
			setPhone(value);
			phoneVal.current = value;
		}
	};
	const handleAreaCodeChange = (option: AreaCodeOption) => {
		// 组件展示“国家名称（区号）”，但验证码和登录接口仍沿用纯区号。
		const nextAreaCode = option.code || option.description || phoneAreaCode;
		setPhoneAreaCode(nextAreaCode);
		// 滑块验证码成功回调读取 phoneAreaCodeVal.current，区号 UI 变更必须同步这个 ref。
		phoneAreaCodeVal.current = nextAreaCode;
		hadChangeLanguage.current = true;
	};
	const areaCodeOptions = sortAreaCodeDataList.map((item) => {
		// 国家名称沿用旧弹层的翻译 key，避免改变国家/地区列表原有内容。
		const areaName =
			(translate &&
				translate[item.country]) ||
			item.value;
		return {
			key: item.key,
			// PhoneInput 输入区展示格式来自新版设计图：国家名称（区号）。
			value: `${areaName}（${item.code}）`,
			label: areaName,
			code: item.code,
			description: item.code,
		};
	});
	const verCodeText = isGetVerCode
		? templateReplace(translate && translate["重新获取 ({SECONDS})"], {
				SECONDS: verCodeSecond,
		  })
		: translate && translate["取得验证码"];
	// 这里只控制“取得验证码”按钮的可点击视觉态，具体格式校验仍在 getVerCodeFn 中执行。
	const canGetVerifyCode = Boolean((isEmailLogin ? email : phone).trim());
	const emailLoginCheckPass = () => {
		return (
			isEmailLogin &&
			emailReg.test(email) &&
			verifyCode &&
			verifyCode.length === 6 &&
			isAgree &&
			isAgreePush
		);
	};
	const phoneLoginCheckPass = () => {
		return (
			!isEmailLogin &&
			phoneReg.test(phone) &&
			verifyCode &&
			verifyCode.length === 6 &&
			isAgree &&
			isAgreePush
		);
	};
	const canReceive = Boolean(phoneLoginCheckPass() || emailLoginCheckPass());
	const { run: onReceive } = useThrottleFn(
		() => {
			// if ( loginType == LOGIN_PHONE && !phoneLoginCheckPass()) {
			//   return false;
			// }
			// if (loginType == LOGIN_EMAIL && !emailLoginCheckPass()) {
			//   return false;
			// }
			if (!(phoneLoginCheckPass() || emailLoginCheckPass())) {
				return;
			}
			if (!isAgree) {
				isCodeType = false;
				HdlToast.info(translate["请勾选同意用户许可使用协议和隐私政策"]);
				return false;
			}
			if (isCodeType) {
				return false;
			}
			isCodeType = true;
			setTimeout(() => {
				isCodeType = false;
			}, 3000);
			loadingRef.current.open();
			// 根据不同的登录方式获取参数
			let params: any = {
				accountType: "0",
				phone,
				areCode: phoneAreaCode,
				verifyCode,
				activityId: activeInfo.current?.activityId,
			};
			if (isEmailLogin) {
				params = {
					accountType: "1",
					verifyCode,
					activityId: activeInfo.current?.activityId,
					mail: email,
				};
			}
			loginAndSend({
				...params,
			})
				.then((res: any) => {
					// console.log("res", res);
					if (res.code == 200) {
						const needToastAreas = ["HK", "MO"];
						// 港澳台需要toast
						if (needToastAreas.includes(country)) {
							HdlToast.info(translate["领取成功"], 2000);
							setTimeout(() => {
								reset();
							}, 1500);
						} else {
							// 弹框提示
							setGetSuccessDialog(true);
						}
					}
					loadingRef.current.close();
				})
				.catch((err) => {
					// console.log({err}, 'err ==============')
					// err.code && HdlToast.info((translate && translate[errorStatus[err.code]]) || err.msg);
					const _errorMsg =
						(err.code &&
							errorStatus[err.code] &&
							translate &&
							translate[errorStatus[err.code]]) ||
						err.msg ||
						err.message;
					HdlToast.info(_errorMsg);
					if (err.code === Time_End) {
						setActFailStatus(true);
					}
					loadingRef.current.close();
				});
		},
		{ wait: 500 },
	);

	const { run: submit } = useThrottleFn(
		async () => {
			loadingRef.current.open();
			try {
				const res: any = await shareCouponSend({
					activityId: activeInfo.current?.activityId,
				});
				if (res.code == 200) {
					const needToastAreas = ["HK", "MO"];
					// 港澳台需要toast
					if (needToastAreas.includes(country)) {
						HdlToast.info(translate["领取成功"], 2000);
						setTimeout(() => {
							reset();
						}, 1500);
					} else {
						// 弹框提示
						setGetSuccessDialog(true);
					}
					trackLogFn(
						"h5_active_share_coupon_success_show",
						"show",
						"H5 领取优惠券-分享优惠券成功",
					);
				}
			} catch (error: any) {
				const _errorMsg =
					(error.code &&
						errorStatus[error.code] &&
						translateVal.current &&
						translateVal.current[errorStatus[error.code]]) ||
          error.msg;
				HdlToast.info(_errorMsg);
			} finally {
				loadingRef.current.close();
			}
		},
		{ wait: 500 },
	);

	// 获取验证码配置时间戳
	const { run: getCaptchaConfigFn } = useThrottleFn(
		() => {
			catptchInstance?.current?.destroy();
			return new Promise<any>((resolve) => {
				getGt4Captcha({})
					.then(async (res: any) => {
						// console.log(res)
						// console.log({aa: (window as any).initGeetest4})
						if (res.code == 200) {
							riskTypeConfig.current = res.data;
							await initCaptcha();
							resolve(true);
						} else {
							HdlToast.info(res.msg);
							resolve(false);
						}
					})
					.catch((err) => {
						console.log("getGt4Captcha, err ==============", err);
						const _errorMsg =
							(err.code &&
								errorStatus[err.code] &&
								translate &&
								translate[errorStatus[err.code]]) ||
							err.msg ||
							err.message;
						HdlToast.info(_errorMsg);
						resolve(false);
					});
			});
		},
		{ wait: 500 },
	);
	const startSecond = () => {
		getVerCodeTimer.current = setInterval(() => {
			if (currentTime.current < 2) {
				setIsGetVerCode(false);
				getVerCodeTimer.current && clearInterval(getVerCodeTimer.current);
				currentTime.current = TIME_DURING;
				setVerCodeSecond(TIME_DURING);
				getCaptchaConfigFn();
				// initCaptcha()
			} else {
				currentTime.current = currentTime.current - 1;
				setVerCodeSecond(currentTime.current);
			}
		}, 1000);
	};
	const initCaptcha = () => {
		return new Promise<any>((resolve, reject) => {
			try {
				catptchInstance.current?.destroy();
				(window as any)?.initGeetest4(
					{
						captchaId: "9a3b611d1312a9862043ceac6fc068b8",
						hideSuccess: true,
						riskType: riskTypeConfig.current,
						product: "bind",
						language: captchaLanguage.current,
						// language: 'ind'
					},
					(captchaObj: any) => {
						catptchInstance.current = captchaObj;
						catptchInstance.current
							?.onReady(() => {
								// console.log('ready')
								resolve(true);
							})
							.onSuccess(async (e: any) => {
								const _getValidate = catptchInstance.current?.getValidate();
								console.log("_getValidate", _getValidate);
								// catptchInstance.current?.reset();
								getVerifyCode({
									accountType: "0",
									lotNumber: _getValidate.lot_number,
									captchaOutput: _getValidate.captcha_output,
									passToken: _getValidate.pass_token,
									genTime: _getValidate.gen_time,
									mobile: phoneVal.current,
									areaCode: phoneAreaCodeVal.current,
								})
									.then((res: any) => {
										if (res.code == 200) {
											setIsGetVerCode(true);
											startSecond();
											HdlToast.info(
												translateVal.current &&
													translateVal.current["发送成功"],
											);
											trackLogFn(
												"h5_active_smscode_success_show",
												"show",
												"H5 领取优惠券-邮箱验证码发送成功",
											);
										}
									})
									.catch(async (err) => {
										console.log("err", err);
										const _errorMsg =
											(err.code &&
												errorStatus[err.code] &&
												translateVal.current &&
												translateVal.current[errorStatus[err.code]]) ||
											err.msg ||
											err.message;
										// err.code && errorStatus[err.code] && HdlToast.info((translateVal.current && translateVal.current[errorStatus[err.code]]) ) ||;
										HdlToast.info(_errorMsg);
									});
							})
							.onClose(async (closeInfo: any) => {
								console.log({ closeInfo }, "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx");
							})
							.onFail(async (failInfo: any) => {
								console.log(
									failInfo,
									catptchInstance.current,
									"catptchInstance.current?.getValidate()==================== onFail error",
								);
								HdlToast.info(
									translateVal.current &&
										translateVal.current["发送验证码错误"],
								);
							})
							.onError(async (error: any) => {
								// setTimeout(() => {
								HdlToast.info(
									error.msg ||
										(translateVal.current &&
											translateVal.current["发送验证码错误"]),
								);
								// }, 1500)
								console.log(
									error,
									"catptchInstance.current?.getValidate()==================== onError error",
									translateVal.current,
								);
								// alert(translateVal.current && translateVal.current['send_code_error'])
								// catptchInstance?.current?.destroy();
								// await getCaptchaConfigFn();
							});
						// captcha为验证码实例
						// captcha.appendTo("#captcha");// 调用appendTo将验证码插入到页的某一个元素中，这个元素用户可以自定义
					},
				);
			} catch (error) {
				resolve(false);
				console.log(
					error,
					"initGeetest4 =======================================",
				);
			}
		});
	};
	const { run: getVerCodeFn } = useThrottleFn(
		async () => {
			// debugger
			if (isGetVerCode) {
				return;
			}
			if (!phoneReg.test(phone) && !isEmailLogin) {
				HdlToast.info(translate && translate["手机号错误，请输入正确的手机号"]);
				return;
			}
			if (!emailReg.test(email) && isEmailLogin) {
				HdlToast.info(translate && translate["邮箱错误，请输入正确的邮箱"]);
				return;
			}

			if (isEmailLogin) {
				if (emailLoginVercodeBtnFlag.current) {
					return;
				}
				emailLoginVercodeBtnFlag.current = true;
				getVerifyCode({
					accountType: "1",
					mail: email,
					activityId: activeInfo.current?.activityId,
				})
					.then((res: any) => {
						// console.log(res, 'res =======================')
						if (res.code == 200) {
							setIsGetVerCode(true);
							emailLoginVercodeBtnFlag.current = false;
							startSecond();
							HdlToast.info(
								translateVal.current && translateVal.current["发送成功"],
							);
							trackLogFn(
								"h5_active_smscode_success_show",
								"show",
								"H5 领取优惠券-短信验证码发送成功",
							);
						}
					})
					.catch(async (err) => {
						// err.code && HdlToast.info((translateVal.current && translateVal.current[errorStatus[err.code]]) || err.msg);
						const _errorMsg =
							(err.code &&
								errorStatus[err.code] &&
								translate &&
								translate[errorStatus[err.code]]) ||
							err.msg ||
							err.message;
						HdlToast.info(_errorMsg);
						emailLoginVercodeBtnFlag.current = false;
					});
				return;
			}

			const _diffTime = Date.now() - catptchInstance?.current?.$_BAGm || 0;
			// console.log({_diffTime, a: catptchInstance.current})
			if (_diffTime < 1800000 && catptchInstance.current) {
				console.log("test ==========", catptchInstance.current);
				try {
					catptchInstance.current?.reset();
					catptchInstance.current?.showCaptcha();
				} catch (error) {
					console.log(error, "try catch ===========");
					await getCaptchaConfigFn();
					//  getVerCodeFn();
				}
			} else {
				console.log("timer out ==================================");
				if (!catptchInstance.current) {
					HdlToast.info(translate && translate["您当前的网络异常，请稍后再试"]);
				}
				await getCaptchaConfigFn();
			}
		},
		{ wait: 500, trailing: false },
	);
	const trackLogFn = (
		identifier: TrackIdentifier,
		type: TrackType,
		eventName: TrackEventName,
		currentLanguage?: string,
	) => {
		trackLog(
			{
				eventName: eventName,
				eventIdentifier: identifier,
				eventType: type,
				eventDetail: {
					active_id: activeInfo.current.activityId || "be_null",
					active_name: activeInfo.current.activityName || "be_null",
					template_id:
						activeInfo.current?.templateIdList?.join(",") || "be_null",
					country_code: activeInfo.current?.country || "be_null",
					country:
						ISO_TO_ZH_LOCALE_KEY[activeInfo.current?.country || ""] ||
						activeInfo.current?.country ||
						"be_null",
				},
				currentLanguage: currentLanguage || languageSelectedInfo?.languageName,
				timestamp: Date.now(),
			},
			{},
		).catch((err) => {
			const _errorMsg =
				(err.code &&
					errorStatus[err.code] &&
					translate &&
					translate[errorStatus[err.code]]) ||
				err.msg ||
				err.message;
			HdlToast.info(_errorMsg);
		});
	};
	const btnSureFn = () => {
    reset();

    if (isInApp()) {
      navigateToCoupon();
		} else if (isLine()) {
			const liffUrl = getLineLiffCouponUrl(
				country || activeInfo.current?.country,
			);
			if (liffUrl) {
				window.location.href = liffUrl;
			}
		} else {
			// https://appdownload-global.hdltest.com/linkTypePage?pageType=myCoupon
			let appCouponPath =
				"https://appdownload.superhi-tech.com/linkTypePage?pageType=myCoupon";
			if (window.location.href.includes(".testsuperhi.com")) {
				appCouponPath =
					"https://appdownload-global.testsuperhi.com/linkTypePage?pageType=myCoupon";
			}
			window.open(appCouponPath);
		}
	};
	return (
		<>
			<HLoading cRef={loadingRef} />
			{actFailStatus ? (
				<div className="page">
					<div>
						<img
							className="empty_coupon"
							src={require("@/images/empty_coupon.png")}
							alt="失败"
						/>
					</div>
					<div className="failText">
						{translate && translate["慢了一步，活动已经结束了"]}
					</div>
				</div>
			) : (
				<div className="receiveCoupon">
					<div className="activeBgBox">
						{detailRuleBg ? (
							<img className="activeBg" src={detailRuleBg} alt="背景图" />
						) : null}
					</div>
					<div
						className="languageBox"
						onClick={() => {
							setLanguageVisible(true);
						}}
					>
						<img
							className="languageIcon"
							src={require("@/images/language-icon.png")}
							alt="海底捞"
						/>
						{languageSelectedInfo?.value}
						<img
							className="rightIcon"
							src={require("@/images/right-icon.png")}
							alt="海底捞"
						/>
					</div>
					<div
						onClick={() => {
							const hdlToken = Cookies.get("hdl_token");
							if (hdlToken) {
								submit();
								return;
							}
							setImmediately(!isImmediately);
							trackLogFn(
								"h5_active_couponpage_click",
								"click",
								"H5 领取优惠券页面按钮点击",
							);
							// setCaptcha(getCaptcha());
						}}
						className="footerDiv"
					>
						<div className="btn">{translate && translate["立即领取"]}</div>
					</div>
					<LoginCouponModal
						visible={isImmediately}
						translate={translate}
						loginType={loginType}
						isEmailLogin={isEmailLogin}
						phone={phone}
						email={email}
						verifyCode={verifyCode}
						phoneAreaCode={phoneAreaCode}
						areaCodeOptions={areaCodeOptions}
						isAgree={isAgree}
						isAgreePush={isAgreePush}
						isGetVerCode={isGetVerCode}
						verCodeText={verCodeText}
						canGetVerifyCode={canGetVerifyCode}
						canReceive={canReceive}
						languageCode={languageSelectedInfo?.languageCode || languageCode.current}
						onClose={closeLoginModal}
						onLoginTypeChange={(value) => selectLoginTypeFn([value])}
						onPhoneChange={handlePhoneChange}
						onEmailChange={setEmail}
						onVerifyCodeChange={setVerifyCode}
						onAreaCodeChange={handleAreaCodeChange}
						onGetVerifyCode={getVerCodeFn}
						onAgreeChange={setAgree}
						onAgreePushChange={setAgreePush}
						onPrivacyClick={() =>
							goToPage("privacy", country, languageCode.current)
						}
						onMemberNoticeClick={() =>
							goToPage("user", country, languageCode.current)
						}
						onReceive={onReceive}
					/>
					<Dialog
						visible={aiTranslateVisible}
						title={(translate && translate["AI 翻译说明"]) }
						contentAlign="left"
						contentClassName="aiTranslateDialogContent"
						cancelText={(translate && translate["取消"])}
						confirmText={(translate && translate["确定"])}
						closeOnOverlayClick={false}
						zIndex={10001}
						className="aiTranslateDialog"
						onCancel={() => {
							setAiTranslateVisible(false);
							applyLanguageInfo(aiTranslateFallbackLanguageInfo);
						}}
						onConfirm={() => {
							setAiTranslateVisible(false);
							applyLanguageInfo(aiTranslateSelectedLanguageInfo);
						}}
					>
						{(translate &&
							translate[
								"您当前选择的语言下的部分内容，可能由 AI 进行翻译。点击「确定」，即表示您已阅读并同意继续使用 AI 翻译内容。"
							])}
					</Dialog>
					<CenterPopup
						visible={languageVisible}
						onMaskClick={() => {
							setLanguageVisible(false);
						}}
						destroyOnClose
					>
						<div className="languagePopBox">
							<CheckList
								className="languagePopList"
								defaultValue={[languageValue]}
								value={[languageValue]}
								onChange={(val) => selectLanguageFn(val)}
								style={{
									"--font-size": $vw(16),
									"--padding-right": $vw(32),
									"--border-bottom": "none",
									"--border-top": "none",
									"--border-inner": "1px solid #f4f4f4",
								}}
								activeIcon={
									<img
										className="languageSelected"
										src={require("@/images/language-selected.png")}
										alt="选择框"
									/>
								}
							>
								{languageSelectList.map((item) => (
									<CheckList.Item
										key={item.value}
										value={item.value}
										className="listItem"
									>
										<div className="languagePopItem">
											<div>{item.value}</div>
										</div>
									</CheckList.Item>
								))}
							</CheckList>
						</div>
					</CenterPopup>
					{getSuccessDialog ? (
						<div
							className="conponMask immediatelyBox"
						>
							<div
								className="immediatelyDiv"
							>
								<div className="title">{translate && translate["领取成功"]}</div>
								<img
									src={require("@/images/hi.png")}
									className="titleHi"
									alt="海底捞"
								/>
								<div className="successDialogBody">

									<div className="unregisterTip">
                      {(isLine() || isInApp()) ? translate?.["感谢您的参与，点击查看优惠券详情！"] : country ==='TW' ? translate?.['开放 Line 海底捞官方账号绑定会员，查看已领取的优惠券'] : translate?.["立即开启海底捞会员 APP，查看已领取的优惠券！"] }
									</div>
									<Divider style={{ width: "100%", marginBottom: $vw(20) }} />
									<div className="footerBtnBox">
										<span className="footerBtn" onClick={() => reset()}>
											{isInApp()
												? translate && translate["返回"]
												: translate && translate["取消"]}
										</span>
										<Divider
											direction="vertical"
											style={{ width: "2px", height: "32px" }}
										/>
										<span className="footerBtn sure" onClick={btnSureFn}>
											{isInApp()
												? translate && translate["查看优惠券"]
												: translate && translate["确定"]}
										</span>
									</div>
								</div>
							</div>
						</div>
					) : null}
				</div>
			)}
		</>
	);
};

export default ReceiveCoupon;
